import { useEffect, useRef, useState } from 'react'
import { Editor } from '@tinymce/tinymce-react'

import { EMPTY_LIST, EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { EVENT_WYSIWYG_FOCUS } from '../../constants/event'
import { useThemeMode, useUserData } from '../../hooks'
import { Skeleton as SkeletonComponent } from '../Skeleton'

import { FileDragOverlay } from './FileDragOverlay'
import { WYSIWYG_EDITOR_DEFAULT_MIN_HEIGHT } from './WysiwygEditor.constants'
import {
  useEditorAddFile,
  useEditorBaseFont,
  useEditorFileLinksBlock,
  useEditorPasteAutolink,
  useEditorScrollCaretIntoView,
  useEditorTablePasteRestriction,
  useEditorThemeContent,
  useEditorToolbarOverflowClose,
} from './WysiwygEditor.hooks'
import {
  addWysiwygEditorBorderValidation,
  getWysiwygEditorContentStyle,
  getWysiwygEditorSkin,
  getWysiwygEditorTableConfig,
  getWysiwygEditorToolbar,
  simulateWysiwygEditorClickEvent,
} from './WysiwygEditor.utils'

import * as styles from './WysiwygEditor.module.css'

/**
 * @property {boolean} [blocked]
 * blocked: behaves like disabled (no clicks, no value changes),
 * but keeps the normal visual style (no opacity change) and
 * shows a "not-allowed" cursor. Unlike "readonly", it should not
 * handle clicks or focus changes at all.
 *
 * @property {boolean} [disabled]
 * disabled: the control is visually and functionally disabled
 */
function WysiwygEditor({
  Skeleton = SkeletonComponent,
  autoFocus,
  blocked,
  caretScrollMarginBottom,
  children,
  contentStyles = EMPTY_OBJECT,
  disabled,
  enableInlineImageAttach = false,
  enablePasteAutolink = false,
  enableTables = false,
  error,
  font,
  linkedFiles,
  maxHeight,
  minHeight = WYSIWYG_EDITOR_DEFAULT_MIN_HEIGHT,
  placeholder,
  resize,
  skeletonHeight,
  textareaName,
  value,
  onBlur,
  onChange,
  onClickFile,
  onDragLeave,
  onDragOver,
  onDrop,
  onFocus,
  onInit,
  onRemoveFileLinksBlock,
}) {
  const userData = useUserData()

  const { theme } = useThemeMode()

  const {
    language: { code: userLanguageCode },
  } = userData

  const editorRef = useRef(null)

  const [initiatedTheme, setInitiatedTheme] = useState(null)

  const initiated = initiatedTheme === theme

  const { defaultFont } = useEditorBaseFont({
    editorInitiated: initiated,
    editorRef,
    font,
  })

  useEditorToolbarOverflowClose({
    editorInitiated: initiated,
    editorRef,
  })

  useEffect(() => {
    const nodes = document.getElementsByClassName('tox-tinymce')

    if (nodes.length > 0) {
      addWysiwygEditorBorderValidation(nodes, error)
    }
  }, [error, initiated])

  function handleInit(e, editor) {
    editorRef.current = editor

    onInit?.(editor, e)

    setInitiatedTheme(theme)
  }

  const toolbar = getWysiwygEditorToolbar({
    enableInlineImageAttach,
    enableTables,
  })

  const tableConfig = getWysiwygEditorTableConfig({ enableTables })

  const plugins = [
    'autoresize',
    'lists',
    'advlist',
    'paste',
    'link',
    'autolink',
    ...(enableTables ? ['table'] : EMPTY_LIST),
    ...(enableInlineImageAttach ? ['image'] : EMPTY_LIST),
  ]

  function handleClick() {
    return simulateWysiwygEditorClickEvent()
  }

  function handleAfterInit(editor) {
    // after the react wrapper's content reset, which moves the caret to start
    if (autoFocus) {
      editor.selection.select(editor.getBody(), true)
      editor.selection.collapse(false)
    }

    // to prevent font options from being hidden
    if (disabled || blocked) {
      editor.setMode('readonly')
    }
  }

  // to prevent font options from being hidden
  useEffect(() => {
    if (initiated) {
      if (disabled || blocked) {
        editorRef.current?.setMode('readonly')
      } else {
        editorRef.current?.setMode('design')
      }
    }
  }, [blocked, disabled, initiated])

  const {
    getInputProps,
    getRootProps,
    handleFocusOut,
    modal,
    setup: setupFiles,
  } = useEditorAddFile({
    editorRef,
    enableInlineImageAttach,
    onChange,
    onDragLeave,
    onDragOver,
    onDrop,
  })

  const { setup: setupTablePasteRestriction } = useEditorTablePasteRestriction({
    enableTables,
  })

  const { setup: setupPasteAutolink } = useEditorPasteAutolink({
    enablePasteAutolink,
  })

  const { setup: setupScrollCaretIntoView } = useEditorScrollCaretIntoView({
    caretScrollMarginBottom,
  })

  const { setup: setupFileLinksBlock } = useEditorFileLinksBlock({
    editorRef,
    initiated,
    linkedFiles,
    onChange,
    onClickFile,
    onRemoveFileLinksBlock,
  })

  const { setup: setupThemeContent } = useEditorThemeContent({ theme })

  function setup(editor) {
    setupFiles(editor)
    setupTablePasteRestriction(editor)
    setupPasteAutolink(editor)
    setupScrollCaretIntoView(editor)
    setupFileLinksBlock(editor)
    setupThemeContent(editor)
  }

  function handleEditorInteraction() {
    document.dispatchEvent(new Event(EVENT_WYSIWYG_FOCUS))
  }

  function handleFocus(event) {
    handleEditorInteraction()

    onFocus?.(event)
  }

  function handleMouseDown() {
    handleEditorInteraction()
  }

  return (
    <FileDragOverlay
      disabled={disabled || blocked}
      enableAttach={enableInlineImageAttach}
      getInputProps={getInputProps}
      getRootProps={getRootProps}
    >
      {modal}
      {!initiated && Skeleton ? (
        <Skeleton
          className={styles.skeleton}
          style={{ height: skeletonHeight || maxHeight || minHeight }}
        />
      ) : null}

      <Editor
        init={{
          auto_focus: autoFocus,
          automatic_uploads: false,
          autoresize_bottom_margin: 0,
          block_unsupported_drop: false,
          content_style: getWysiwygEditorContentStyle({
            blocked,
            contentStyles,
            defaultFont,
            theme,
          }),
          elementpath: false,
          entity_encoding: 'raw',
          file_picker_types: EMPTY_STRING,
          font_formats:
            'Arial=Arial,sans-serif; Calibri=calibri,sans-serif; Cambria=Cambria; Monospace=monospace; Plus Jakarta Sans=Plus Jakarta Sans,sans-serif; Sans Serif=sans-serif; Serif=serif; Times New Roman=Times New Roman,sans-serif;  Verdana=Verdana,sans-serif',
          fontsize_formats: '8px 9px 10px 11px 12px 14px 16px',
          init_instance_callback: handleAfterInit,
          language: userLanguageCode,
          link_context_toolbar: true,
          link_title: false,
          max_height: maxHeight,
          menubar: false,
          min_height: minHeight,
          object_resizing: enableInlineImageAttach,
          paste_data_images: enableInlineImageAttach,
          placeholder,
          plugins,
          relative_urls: false,
          remove_script_host: false,
          resize,
          setup,
          skin: getWysiwygEditorSkin(theme),
          target_list: false,
          textareaName,
          toolbar,
          toolbar_groups: {
            alignroup: {
              icon: 'align-left',
              items: 'alignleft aligncenter alignright alignjustify',
            },
          },
          ...tableConfig,
        }}
        // TinyMCE reads the skin only on init, so a theme switch re-creates it.
        key={theme}
        tinymceScriptSrc='https://files.idwell.com/js/tinymce/tinymce.min.js'
        value={value}
        onBlur={onBlur}
        onClick={handleClick}
        onEditorChange={onChange}
        onFocus={handleFocus}
        onFocusOut={enableInlineImageAttach ? handleFocusOut : undefined}
        onInit={handleInit}
        onMouseDown={handleMouseDown}
        // onFocusIn={props.setFocused}
      />
      {children}
    </FileDragOverlay>
  )
}

export default WysiwygEditor
